import { useState, Fragment } from 'react';
import { Check, X, Download, ChevronDown, ChevronRight, Copy, CheckCircle } from 'lucide-react';
import { featureCategories, exportFeaturesToCSV, getFeatureCounts } from '@/lib/features';
import { cn } from '@/lib/utils';

export default function FeatureTable() {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(featureCategories.map((c) => c.name))
  );
  const [copied, setCopied] = useState(false);
  const counts = getFeatureCounts();

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedCategories(new Set(featureCategories.map((c) => c.name)));
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  const handleExportCSV = () => {
    const csv = exportFeaturesToCSV(featureCategories);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'blazepos-features.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyToClipboard = async () => {
    const csv = exportFeaturesToCSV(featureCategories);
    try {
      await navigator.clipboard.writeText(csv);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Feature Comparison
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {counts.total} features across {featureCategories.length} categories
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Collapse All
            </button>
            <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />
            <button
              onClick={handleCopyToClipboard}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              {copied ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy
                </>
              )}
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Feature Counts Summary */}
      <div className="grid grid-cols-3 border-b border-gray-200 dark:border-gray-700">
        <div className="p-4 text-center border-r border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{counts.entry}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Entry Features</div>
        </div>
        <div className="p-4 text-center border-r border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-blue-600">{counts.core}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Core Features</div>
        </div>
        <div className="p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">{counts.pro}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Pro Features</div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 w-1/2">
                Feature
              </th>
              <th className="text-center py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 w-1/6">
                Entry
              </th>
              <th className="text-center py-3 px-4 text-sm font-medium text-blue-600 w-1/6">
                Core
              </th>
              <th className="text-center py-3 px-4 text-sm font-medium text-purple-600 w-1/6">
                Pro
              </th>
            </tr>
          </thead>
          <tbody>
            {featureCategories.map((category) => {
              const isExpanded = expandedCategories.has(category.name);
              const categoryFeatureCount = category.features.length;
              const entryCount = category.features.filter((f) => f.entry).length;
              const coreCount = category.features.filter((f) => f.core).length;
              const proCount = category.features.filter((f) => f.pro).length;

              return (
                <Fragment key={category.name}>
                  {/* Category Header */}
                  <tr
                    className="border-b border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 cursor-pointer hover:bg-gray-150 dark:hover:bg-gray-750"
                    onClick={() => toggleCategory(category.name)}
                  >
                    <td className="py-3 px-4 w-1/2">
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        )}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {category.name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({categoryFeatureCount} features)
                        </span>
                      </div>
                    </td>
                    <td className="text-center py-3 px-4 text-sm text-gray-500 dark:text-gray-400 w-1/6">
                      {entryCount}/{categoryFeatureCount}
                    </td>
                    <td className="text-center py-3 px-4 text-sm text-blue-600 w-1/6">
                      {coreCount}/{categoryFeatureCount}
                    </td>
                    <td className="text-center py-3 px-4 text-sm text-purple-600 w-1/6">
                      {proCount}/{categoryFeatureCount}
                    </td>
                  </tr>

                  {/* Features */}
                  {isExpanded &&
                    category.features.map((feature, index) => (
                      <tr
                        key={feature.name}
                        className={cn(
                          'border-b border-gray-100 dark:border-gray-700/50',
                          index % 2 === 0
                            ? 'bg-white dark:bg-gray-800'
                            : 'bg-gray-50/50 dark:bg-gray-800/50'
                        )}
                      >
                        <td className="py-3 px-4 pl-10 w-1/2">
                          <div>
                            <div className="text-sm text-gray-900 dark:text-white">
                              {feature.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {feature.description}
                            </div>
                          </div>
                        </td>
                        <td className="text-center py-3 px-4 w-1/6">
                          {feature.entry ? (
                            <Check className="h-5 w-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="h-5 w-5 text-gray-300 dark:text-gray-600 mx-auto" />
                          )}
                        </td>
                        <td className="text-center py-3 px-4 w-1/6">
                          {feature.core ? (
                            <Check className="h-5 w-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="h-5 w-5 text-gray-300 dark:text-gray-600 mx-auto" />
                          )}
                        </td>
                        <td className="text-center py-3 px-4 w-1/6">
                          {feature.pro ? (
                            <Check className="h-5 w-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="h-5 w-5 text-gray-300 dark:text-gray-600 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
